import React, { useState, useCallback, useRef } from 'react';
import { Box, Typography, Button, CircularProgress, Stack } from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Outfit } from '../../types';
import { PlaybackSpeed, NarratorVoice, ReelState } from '../../types/fashion-show';
import OutfitService from '../../services/outfit-service';
import ReelSetupCard from './ReelSetupCard';
import ReelPlayer from './ReelPlayer';

export interface FashionShowReelProps {
  onClose: () => void;
}

/**
 * Container component that orchestrates the entire Fashion Show Reel feature.
 *
 * Manages the state machine: setup → loading → playing → completed → error.
 * In setup state, renders ReelSetupCard. On start, fetches outfits via
 * OutfitService.getOutfitsByEventType. On success with outfits, transitions
 * to playing and renders ReelPlayer. Handles empty results, fetch errors,
 * completion, and exit.
 *
 * Requirements: 1.1, 2.1, 2.2, 2.3, 2.4, 3.4, 7.4
 */
const FashionShowReel: React.FC<FashionShowReelProps> = ({ onClose }) => {
  const [reelState, setReelState] = useState<ReelState>('setup');
  const [outfits, setOutfits] = useState<Outfit[]>([]);
  const [speed, setSpeed] = useState<PlaybackSpeed>('normal');
  const [voice, setVoice] = useState<NarratorVoice>('male');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [selectedEventType, setSelectedEventType] = useState<string>('');
  const mountedRef = useRef<boolean>(true);

  // Cleanup on unmount
  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleStart = useCallback(async (eventType: string, selectedSpeed: PlaybackSpeed, selectedVoice: NarratorVoice) => {
    setSelectedEventType(eventType);
    setSpeed(selectedSpeed);
    setVoice(selectedVoice);
    setReelState('loading');
    setErrorMessage('');

    try {
      const fetchedOutfits = await OutfitService.getOutfitsByEventType(eventType, 100);

      if (!mountedRef.current) return;

      if (fetchedOutfits.length === 0) {
        setReelState('error');
        setErrorMessage(`No outfits available for "${eventType}". Try a different event type.`);
        return;
      }

      setOutfits(fetchedOutfits);
      setReelState('playing');
    } catch (error: any) {
      if (!mountedRef.current) return;
      console.error('[FashionShowReel] Failed to fetch outfits:', error);
      setReelState('error');
      setErrorMessage(
        error?.message || 'Failed to load outfits. Please check your connection and try again.'
      );
    }
  }, []);

  const handleRetry = useCallback(() => {
    if (selectedEventType) {
      handleStart(selectedEventType, speed, voice);
    } else {
      setReelState('setup');
    }
  }, [selectedEventType, speed, handleStart]);

  const handleComplete = useCallback(() => {
    setReelState('setup');
    setOutfits([]);
  }, []);

  const handleExit = useCallback(() => {
    setOutfits([]);
    onClose();
  }, [onClose]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleBackToSetup = useCallback(() => {
    setReelState('setup');
    setOutfits([]);
    setErrorMessage('');
  }, []);

  // Setup state: show the configuration card
  if (reelState === 'setup') {
    return (
      <ReelSetupCard onStart={handleStart} onCancel={handleCancel} />
    );
  }

  // Loading state: show loading indicator
  if (reelState === 'loading') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 300,
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">
          Loading outfits for "{selectedEventType}"...
        </Typography>
      </Box>
    );
  }

  // Playing state: render the full-screen ReelPlayer
  if (reelState === 'playing') {
    return (
      <ReelPlayer
        outfits={outfits}
        speed={speed}
        voice={voice}
        onComplete={handleComplete}
        onExit={handleExit}
      />
    );
  }

  // Error / empty state
  if (reelState === 'error') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 300,
          gap: 2,
          textAlign: 'center',
          px: 3,
        }}
      >
        <Typography variant="h6" color="text.primary">
          {errorMessage.includes('No outfits') ? '👗' : '⚠️'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {errorMessage}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToSetup}
          >
            Back to Setup
          </Button>
          {!errorMessage.includes('No outfits') && (
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={handleRetry}
            >
              Retry
            </Button>
          )}
        </Stack>
      </Box>
    );
  }

  // Completed state — same as returning to setup
  return (
    <ReelSetupCard onStart={handleStart} onCancel={handleCancel} />
  );
};

export default FashionShowReel;
